// TenantIntegrationsPage.jsx — supervisor/admin-facing page for
// checking (and, for admins, editing) the GymSales connection.
//
// Language is deliberately plain.
//   • "Connection" not "credentials"
//   • "Login" / "Password" not "api_username" / "api_password"
//   • "Test connection" not "probe endpoint"
//   • "Router activity" widget uses copy like "Waits" (429s) and
//     "Retries that worked" (retry_wins) so it reads without a manual.
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import Layout from '../../components/custom/Layout';
import { useAuth } from '../../contexts/AuthContext';
import {
  Link2, CheckCircle2, XCircle, Loader2, Save, Eye, EyeOff,
  RefreshCw, AlertCircle, KeyRound, Info,
} from 'lucide-react';
import RouterMetricsCard from '../../components/gymsales/RouterMetricsCard';
import RouterPingCard from '../../components/gymsales/RouterPingCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ── Connection Card ─────────────────────────────────────────────── */

const ConnectionCard = ({ onAvailability }) => {
  const { token } = useAuth();
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [creds, setCreds] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ enabled: true, api_username: '', api_password: '', base_url: '' });
  const [sites, setSites] = useState([]);
  const [newSiteId, setNewSiteId] = useState('');
  const [newSiteLabel, setNewSiteLabel] = useState('');

  const addSiteId = () => {
    const sid = newSiteId.trim();
    if (!sid || sites.some((s) => s.id === sid)) { setNewSiteId(''); return; }
    setSites([...sites, { id: sid, label: newSiteLabel.trim(), enabled: false }]);
    setNewSiteId('');
    setNewSiteLabel('');
  };

  const toggleSite = (sid) => setSites(sites.map((s) => (s.id === sid ? { ...s, enabled: !s.enabled } : s)));
  const removeSite = (sid) => setSites(sites.filter((s) => s.id !== sid));
  const [showPw, setShowPw] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState('');

  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [testErr, setTestErr] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const r = await axios.get(`${API}/tenant/gymsales/creds`, { headers });
      setCreds(r.data);
      onAvailability?.(r.data.available !== false);
      setForm({
        enabled: true,
        api_username: r.data.username || '',
        api_password: '',                 // never prefilled
        base_url: r.data.base_url || '',
      });
      setSites(r.data.site_registry || []);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Could not load the connection.');
    } finally { setLoading(false); }
  }, [headers]);

  useEffect(() => { load(); }, [load]);

  const runTest = useCallback(async (candidate = false) => {
    setTesting(true); setTestResult(null); setTestErr('');
    try {
      const body = candidate && creds?.can_edit
        ? {
            api_username: form.api_username.trim(),
            api_password: form.api_password || undefined,
            base_url: form.base_url?.trim() || undefined,
          }
        : {};
      const r = await axios.post(`${API}/tenant/gymsales/test`, body, { headers });
      setTestResult(r.data);
    } catch (e) {
      setTestErr(e?.response?.data?.detail || 'Test failed.');
    } finally { setTesting(false); }
  }, [creds, form, headers]);

  const save = useCallback(async () => {
    setSaving(true); setSaveErr('');
    try {
      await axios.patch(`${API}/tenant/gymsales/creds`, {
        enabled: form.enabled,
        api_username: form.api_username.trim(),
        api_password: form.api_password || undefined,
        site_registry: sites,
        company_id: sites.filter((s) => s.enabled).map((s) => s.id).join(','),
        base_url: form.base_url?.trim() || undefined,
      }, { headers });
      setEditing(false);
      setForm((f) => ({ ...f, api_password: '' }));
      await load();
    } catch (e) {
      setSaveErr(e?.response?.data?.detail || 'Save failed.');
    } finally { setSaving(false); }
  }, [form, sites, headers, load]);

  if (loading) {
    return (
      <div className="bg-white shadow-sm border border-slate-200 rounded-xl p-6 flex items-center justify-center min-h-[120px]">
        <Loader2 className="w-5 h-5 text-slate-400 animate-spin" />
      </div>
    );
  }
  if (err) {
    return (
      <div className="bg-rose-50 border border-rose-200 rounded-xl p-4 text-rose-800 text-sm flex items-start gap-2">
        <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />{err}
      </div>
    );
  }

  const canEdit = !!creds?.can_edit;
  const isConnected = !!creds?.connected;

  // OPT-IN (Sep 2026): domains without a provisioned GymSales integration
  // get NO GymSales surface at all — not even a disabled card.
  if (creds?.available === false) return null;

  return (
    <div className="bg-white shadow-sm border border-slate-200 rounded-xl overflow-hidden" data-testid="gymsales-connection-card">
      {/* Header */}
      <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-3">
        <Link2 className="w-4 h-4 text-emerald-600" />
        <div className="flex-1">
          <div className="text-slate-900 font-semibold text-sm">GymSales connection</div>
          <div className="text-[11px] text-slate-500">
            Sends your callers&apos; phone numbers to GymSales so live journey history shows up on every call.
          </div>
        </div>
        {isConnected ? (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-600 border border-emerald-300" data-testid="connection-status-ok">
            <CheckCircle2 className="w-3.5 h-3.5" />Connected
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-600 border border-slate-300" data-testid="connection-status-off">
            <XCircle className="w-3.5 h-3.5" />Not connected
          </span>
        )}
      </div>

      {/* Body — view mode */}
      {!editing && (
        <div className="p-4 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
            <Field label="Login" value={creds.username || '—'} testid="field-username" />
            <Field
              label="Password"
              value={creds.password_saved ? '••••••••••' : '(not set)'}
              testid="field-password"
            />
            <Field
              label="Sites"
              value={(creds.site_registry || []).length
                ? creds.site_registry.map((s) => `${s.label || s.id} ${s.enabled ? '(on)' : '(off)'}`).join(' · ')
                : (creds.company_id || '—')}
              testid="field-company-id"
            />
            <Field label="Server URL" value={creds.base_url || 'Standard GymSales'} testid="field-base-url" mono />
          </div>

          {creds.source === 'env' && (
            <div className="bg-amber-50 border border-amber-300 rounded p-2.5 text-[11px] text-amber-800 flex items-start gap-2">
              <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              You&apos;re currently using the shared default GymSales login. To connect your own GymSales account, {canEdit ? 'click "Change details" below.' : 'ask your domain admin to add your account details here.'}
            </div>
          )}

          {creds.updated_at && (
            <div className="text-[11px] text-slate-400">
              Last updated {new Date(creds.updated_at).toLocaleString()}
              {creds.updated_by_name ? ` by ${creds.updated_by_name}` : ''}
            </div>
          )}

          <div className="flex flex-wrap gap-2 pt-1">
            <button
              type="button"
              onClick={() => runTest(false)}
              disabled={testing}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 text-slate-900 font-semibold rounded"
              data-testid="test-connection-btn"
            >
              {testing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
              Test connection
            </button>
            {canEdit && (
              <button
                type="button"
                onClick={() => setEditing(true)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-slate-100 hover:bg-slate-200 text-slate-900 font-semibold rounded"
                data-testid="edit-connection-btn"
              >
                <KeyRound className="w-3.5 h-3.5" />Change details
              </button>
            )}
          </div>

          <TestResult result={testResult} err={testErr} />
        </div>
      )}

      {/* Body — edit mode */}
      {editing && canEdit && (
        <div className="p-4 space-y-3" data-testid="edit-connection-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
            <div>
              <label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Login</label>
              <input
                type="text"
                value={form.api_username}
                onChange={(e) => setForm({ ...form, api_username: e.target.value })}
                className="mt-1 w-full bg-slate-50 border border-slate-200 rounded px-3 py-1.5 text-slate-900 placeholder-slate-400"
                placeholder="e.g. bookings@yourgym.com.au"
                data-testid="input-username"
              />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Password</label>
              <div className="relative mt-1">
                <input
                  type={showPw ? 'text' : 'password'}
                  value={form.api_password}
                  onChange={(e) => setForm({ ...form, api_password: e.target.value })}
                  className="w-full bg-slate-50 border border-slate-200 rounded px-3 py-1.5 text-slate-900 placeholder-slate-400 pr-9"
                  placeholder={creds.password_saved ? 'Leave blank to keep current password' : ''}
                  data-testid="input-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPw((s) => !s)}
                  className="absolute inset-y-0 right-2 flex items-center text-slate-500 hover:text-slate-900"
                  data-testid="toggle-password-visibility"
                >
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
                GymSales Sites <span className="text-slate-400 normal-case">(toggle each club on/off — only ON clubs are searched)</span>
              </label>
              <div className="mt-1.5 space-y-1.5" data-testid="site-id-chips">
                {sites.map((s) => (
                  <div key={s.id} className={`flex items-center justify-between rounded-lg border px-3 py-2 ${s.enabled ? 'border-emerald-200 bg-emerald-50/50' : 'border-slate-200 bg-slate-50'}`} data-testid={`site-row-${s.id}`}>
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="font-mono text-xs font-semibold text-slate-700">{s.id}</span>
                      {s.label && <span className="text-xs text-slate-500 truncate">{s.label}</span>}
                      <span className={`text-[10px] font-bold uppercase tracking-wide ${s.enabled ? 'text-emerald-600' : 'text-slate-400'}`}>{s.enabled ? 'On' : 'Off'}</span>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <button
                        type="button"
                        onClick={() => toggleSite(s.id)}
                        className={`relative w-9 h-5 rounded-full transition-colors ${s.enabled ? 'bg-emerald-500' : 'bg-slate-300'}`}
                        data-testid={`toggle-site-${s.id}`}
                        title={s.enabled ? 'Turn off — stop searching this club' : 'Turn on — include this club in searches'}
                      >
                        <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all ${s.enabled ? 'left-[18px]' : 'left-0.5'}`} />
                      </button>
                      <button
                        type="button"
                        onClick={() => removeSite(s.id)}
                        className="text-slate-400 hover:text-rose-600 font-bold text-sm leading-none"
                        data-testid={`remove-site-${s.id}`}
                        title={`Remove site ${s.id}`}
                      >
                        ×
                      </button>
                    </div>
                  </div>
                ))}
                <div className="flex items-center gap-1.5 pt-1">
                  <input
                    type="text"
                    value={newSiteId}
                    onChange={(e) => setNewSiteId(e.target.value.replace(/[^0-9]/g, ''))}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addSiteId(); } }}
                    className="w-24 bg-slate-50 border border-slate-200 rounded px-2 py-1 text-slate-900 placeholder-slate-400 font-mono text-xs"
                    placeholder="ID e.g. 4809"
                    data-testid="input-new-site-id"
                  />
                  <input
                    type="text"
                    value={newSiteLabel}
                    onChange={(e) => setNewSiteLabel(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addSiteId(); } }}
                    className="w-40 bg-slate-50 border border-slate-200 rounded px-2 py-1 text-slate-900 placeholder-slate-400 text-xs"
                    placeholder="Label e.g. Docklands"
                    data-testid="input-new-site-label"
                  />
                  <button
                    type="button"
                    onClick={addSiteId}
                    disabled={!newSiteId.trim()}
                    className="text-xs font-semibold px-2.5 py-1 rounded bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-40"
                    data-testid="add-site-id-btn"
                  >
                    + Add site
                  </button>
                </div>
              </div>
              <p className="mt-1 text-[10px] text-slate-500">
                New sites are added OFF — flip the toggle when the club is ready. Searches run across every ON club in order (e.g. Chermside 2551 → Morningside 4809).
              </p>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
                Server URL <span className="text-slate-400 normal-case">(optional)</span>
              </label>
              <input
                type="text"
                value={form.base_url}
                onChange={(e) => setForm({ ...form, base_url: e.target.value })}
                className="mt-1 w-full bg-slate-50 border border-slate-200 rounded px-3 py-1.5 text-slate-900 placeholder-slate-400 font-mono text-xs"
                placeholder="Leave blank for the standard GymSales server"
                data-testid="input-base-url"
              />
            </div>
          </div>

          {saveErr && (
            <div className="bg-rose-50 border border-rose-200 rounded p-2 text-rose-800 text-xs flex items-start gap-2">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />{saveErr}
            </div>
          )}

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => runTest(true)}
              disabled={testing || !form.api_username}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-slate-100 hover:bg-slate-200 disabled:opacity-40 text-slate-900 rounded"
              data-testid="test-before-save-btn"
            >
              {testing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
              Test these details
            </button>
            <button
              type="button"
              onClick={save}
              disabled={saving || !form.api_username}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-emerald-500 hover:bg-emerald-400 disabled:opacity-40 text-slate-900 font-semibold rounded"
              data-testid="save-connection-btn"
            >
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
              Save changes
            </button>
            <button
              type="button"
              onClick={() => { setEditing(false); setForm({ ...form, api_password: '' }); }}
              disabled={saving}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-transparent hover:bg-slate-50 text-slate-600 hover:text-slate-900 rounded"
              data-testid="cancel-edit-btn"
            >
              Cancel
            </button>
          </div>

          <TestResult result={testResult} err={testErr} />
        </div>
      )}
    </div>
  );
};

const Field = ({ label, value, mono, testid }) => (
  <div>
    <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
    <div className={`text-slate-900 text-sm mt-0.5 ${mono ? 'font-mono text-[12px] break-all' : ''}`} data-testid={testid}>
      {value}
    </div>
  </div>
);

const TestResult = ({ result, err }) => {
  if (err) {
    return (
      <div className="bg-rose-50 border border-rose-200 rounded p-2.5 text-rose-800 text-xs flex items-start gap-2" data-testid="test-result-error">
        <XCircle className="w-4 h-4 mt-0.5 shrink-0" />{err}
      </div>
    );
  }
  if (!result) return null;
  if (result.working) {
    return (
      <div className="bg-emerald-50 border border-emerald-200 rounded p-2.5 text-emerald-800 text-xs" data-testid="test-result-ok">
        <div className="flex items-start gap-2">
          <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
          <div>
            <div className="font-semibold">Connection working</div>
            <div className="text-emerald-700 text-[11px] mt-0.5">
              Responded in {Math.round(result.latency_ms || 0)}ms
              {result.company_name ? ` · Signed in as "${result.company_name}"` : ''}
            </div>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="bg-rose-50 border border-rose-200 rounded p-2.5 text-rose-800 text-xs" data-testid="test-result-fail">
      <div className="flex items-start gap-2">
        <XCircle className="w-4 h-4 mt-0.5 shrink-0" />
        <div className="min-w-0 flex-1">
          <div className="font-semibold">Connection failed</div>
          <div className="text-rose-700 text-[11px] mt-0.5">
            {result.problem || `Status ${result.status_code}`}
          </div>
          {result.router_request_id && (
            <div className="mt-1.5 flex items-center gap-1.5 text-[10px] font-mono text-rose-700/80">
              request-id:
              <code className="bg-rose-100 border border-rose-200 px-1 py-0.5 rounded select-all" data-testid="test-result-request-id">
                {result.router_request_id}
              </code>
              <button
                type="button"
                onClick={() => navigator.clipboard?.writeText(result.router_request_id)}
                className="text-rose-700 hover:text-rose-900 underline underline-offset-2 font-sans"
                data-testid="test-result-copy-request-id"
              >
                copy
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/* ── Page ────────────────────────────────────────────────────────── */

const TenantIntegrationsPage = () => {
  const [gsAvailable, setGsAvailable] = useState(true);
  return (
    <Layout>
      <div className="p-6 max-w-5xl mx-auto space-y-4" data-testid="tenant-integrations-page">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Link2 className="w-6 h-6 text-emerald-600" />
            Integrations
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Check the connections your site uses. Test them, and — if you&apos;re a domain admin — update the details.
          </p>
        </div>

        <ConnectionCard onAvailability={setGsAvailable} />

        {gsAvailable ? (
          <>
            <RouterMetricsCard
              endpoint="/tenant/gymsales/router-metrics"
              title="Router activity — last 24 hours"
            />

            <RouterPingCard
              endpoint="/tenant/gymsales/router-pings"
              title="Hourly connection check"
              limit={48}
            />
          </>
        ) : (
          <div className="bg-white border border-slate-200 rounded-xl p-6 text-sm text-slate-500" data-testid="no-integrations-message">
            No integrations are configured for this site.
          </div>
        )}
      </div>
    </Layout>
  );
};

export default TenantIntegrationsPage;
